import { DateRangeSelector } from '@app/components/date-range-selector/date-range/DateRangeSelector';
import type {
  DateRangePreset,
  DateRangeValue,
} from '@app/components/date-range-selector/date-range/date-range-presets';
import {
  getPresetRange,
  isCustomPreset,
} from '@app/components/date-range-selector/date-range/date-range-presets';
import type { DateRangeFilterValue } from '@app/components/date-range-selector/date-range/parse-as-date-range-filter';
import { useTimezone } from '@app/core/hooks/useTimezone';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { GenericParser, useQueryState } from 'nuqs';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useListViewContext } from '../context/ListViewContext';

/**
 * Props for the generic date range filter component.
 */
export interface GenericDateRangeFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label (shown above the selector). Pass false to hide the label. */
  label: MessageDescriptor | false;
  /** Parser for reading/writing this filter value from/to URL (from FilterDefinition). */
  parser: GenericParser<DateRangeFilterValue | null>;
  /** Whether the filter is active (addable filters). Undefined for permanent filters. */
  isActive?: boolean;
  /** Callback when filter is cleared (addable filters). */
  onRemove?: () => void;
  /** Presets available in the date selector menu. */
  allowedPresets?: DateRangePreset[];
  /** Custom styles for the container */
  sx?: SystemStyleObject<Theme>;
}

/**
 * Derives DateRangeValue + preset only for DateRangeSelector UI (trigger label and custom dialogs).
 * The filter value remains preset-based; we do not convert date range to preset.
 */
function presetValueToSelectorState(
  urlValue: DateRangeFilterValue | null,
  timezone: string,
): { value: DateRangeValue | null; preset: DateRangePreset | null } {
  if (urlValue == null) {
    return { value: null, preset: null };
  }

  const { preset, from, to } = urlValue;
  if (isCustomPreset(preset) && from != null && to != null) {
    return {
      value: { from: new Date(from), to: new Date(to) },
      preset,
    };
  }

  const range = getPresetRange(preset, timezone);
  return { value: range, preset };
}

/**
 * Generic date range filter. The filter value is the preset (for custom presets also from/to).
 * Uses DateRangeSelector for UI only; list/API receives the preset, not a converted date range.
 */
export function GenericDateRangeFilter({
  name,
  label,
  parser,
  isActive,
  onRemove,
  allowedPresets,
  sx,
}: GenericDateRangeFilterProps) {
  const { $t } = useIntl();
  const tz = useTimezone();
  const { setFilterValue } = useListViewContext();

  const [urlValue] = useQueryState(name, parser) as [DateRangeFilterValue | null, never];
  const { value, preset } = presetValueToSelectorState(urlValue, tz);

  const handleChange = (range: DateRangeValue, newPreset: DateRangePreset) => {
    // Filter value is the preset (and for custom presets, from/to as well)
    const next: DateRangeFilterValue = {
      preset: newPreset,
      ...(isCustomPreset(newPreset) && {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      }),
    };
    setFilterValue(name, next);
  };

  const handleClear = () => {
    setFilterValue(name, null);
  };

  if (isActive === false && !urlValue) {
    return null;
  }

  const hasValue = urlValue != null;
  // Inner clear only for addable filters (onRemove is set). Permanent filters (e.g. "ÚLTIMOS 30 DÍAS") do not show clear.
  const showClear = hasValue && onRemove != null;

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          minWidth: '18ch',
        },
        sx ?? {},
      ]}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {label !== false && (
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.75rem',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            {$t(label)}
          </Box>
        )}
        <DateRangeSelector
          value={value ?? undefined}
          preset={preset ?? undefined}
          onChange={handleChange}
          allowedPresets={allowedPresets}
          buttonProps={{
            variant: 'outlined',
            color: 'secondary',
            sx: { width: '100%', backgroundColor: 'background.paper', minHeight: '40px' },
          }}
        />
      </Box>
      {showClear && (
        <IconButton
          aria-label={$t({
            id: 'common.clear',
            defaultMessage: 'Clear',
          })}
          onClick={handleClear}
          size="small"
          disabled={!hasValue}
          sx={{
            mt: label === false ? 0.75 : 2.25,
            flexShrink: 0,
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
